import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Edit2,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronRight,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  X,
} from "lucide-react";
import { api } from "@/api/client";
import ConfirmModal from "@/components/common/ConfirmModal";
import { useConfirmModal } from "@/hooks/useConfirmModal";

interface MasterItem {
  id: string;
  kode?: string;
  nama: string;
  tipe?: string;
  deskripsi?: string;
  urutan?: number;
  is_active?: boolean;
  parent_id?: string | null;
}

// Master entities that do NOT have a "kode" column
const TABS_WITHOUT_KODE = new Set(["pic", "stakeholder"]);
// Master entities that have a "tipe" column
const TABS_WITH_TIPE = new Set(["stakeholder"]);
// Master entities that support parent-child hierarchy
const TABS_WITH_PARENT = new Set(["stakeholder"]);

const TABS = [
  // Jenis PNBP dikelola lewat Master Taxonomy PNBP (sumber tunggal) — diturunkan
  // otomatis dari node taxonomy. Tab manual disembunyikan (bukan dihapus).
  // { key: "jenis-pnbp", label: "Jenis PNBP" },
  { key: "siklus-keuangan", label: "Siklus Keuangan" },
  { key: "bentuk-dokumen", label: "Bentuk Dokumen" },
  { key: "dimensi", label: "Dimensi" },
  { key: "kategori-informasi", label: "Kategori Informasi" },
  { key: "status-dokumen", label: "Status Dokumen" },
  { key: "unit-kerja", label: "Unit Kerja" },
  { key: "pic", label: "PIC" },
  { key: "stakeholder", label: "Stakeholder" },
];

function TipeBadge({ tipe }: { tipe?: string }) {
  if (!tipe) return <span className="text-gray-300 text-xs">—</span>;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
      tipe === "INTERNAL" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"
    }`}>
      {tipe}
    </span>
  );
}

function RowActions({
  item,
  onEdit,
  onDelete,
}: {
  item: MasterItem;
  onEdit: (item: MasterItem) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        onClick={() => onEdit(item)}
        className="rounded p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50"
      >
        <Edit2 className="h-4 w-4" />
      </button>
      <button
        onClick={() => onDelete(item.id)}
        className="rounded p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function MasterDataPage() {
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<MasterItem | null>(null);
  const [formData, setFormData] = useState({
    kode: "",
    nama: "",
    tipe: "",
    deskripsi: "",
    urutan: 1,
    parent_id: "",
  });
  const hasTipe = TABS_WITH_TIPE.has(activeTab);
  const hasParent = TABS_WITH_PARENT.has(activeTab);
  const [sortKode, setSortKode] = useState<"asc" | "desc" | null>(null);
  const [sortNama, setSortNama] = useState<"asc" | "desc" | null>(null);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title: string; message: string }>({
    open: false,
    title: "",
    message: "",
  });
  const queryClient = useQueryClient();
  const { modalProps, confirm } = useConfirmModal();
  const hasKode = !TABS_WITHOUT_KODE.has(activeTab);

  const { data: items = [], isLoading } = useQuery<MasterItem[]>({
    queryKey: ["master", activeTab],
    queryFn: () => api.get(`/master/${activeTab}`),
  });

  // Auto-expand all parents when hierarchy data first loads or tab changes
  useEffect(() => {
    if (hasParent && items.length > 0) {
      const parentIds = new Set(items.filter((i) => !i.parent_id).map((i) => i.id));
      setExpandedParents(parentIds);
    }
  }, [items, hasParent]);

  const sortedItems = useMemo(() => {
    if (hasKode && sortKode !== null) {
      return [...items].sort((a, b) => {
        const ka = a.kode ?? "";
        const kb = b.kode ?? "";
        return sortKode === "asc" ? ka.localeCompare(kb) : kb.localeCompare(ka);
      });
    }
    if (sortNama !== null) {
      return [...items].sort((a, b) =>
        sortNama === "asc" ? a.nama.localeCompare(b.nama) : b.nama.localeCompare(a.nama)
      );
    }
    return items;
  }, [items, hasKode, sortKode, sortNama]);

  // Build tree structure for stakeholder — parents with their children grouped
  const treeGroups = useMemo(() => {
    if (!hasParent) return null;
    const topLevel = sortedItems.filter((i) => !i.parent_id);
    const childMap = new Map<string, MasterItem[]>();
    for (const item of sortedItems) {
      if (item.parent_id) {
        const list = childMap.get(item.parent_id) ?? [];
        list.push(item);
        childMap.set(item.parent_id, list);
      }
    }
    // Orphaned children whose parent isn't in the list
    const allParentIds = new Set(topLevel.map((p) => p.id));
    const orphans = sortedItems.filter((i) => i.parent_id && !allParentIds.has(i.parent_id));
    return { topLevel, childMap, orphans };
  }, [sortedItems, hasParent]);

  // Flat list for non-hierarchy tabs
  const flatItems = useMemo(() => {
    if (hasParent) return [];
    return sortedItems.map((item) => ({ item, level: 0 }));
  }, [sortedItems, hasParent]);

  // All parent IDs (for expand/collapse all)
  const allParentIds = useMemo(
    () => new Set((treeGroups?.topLevel ?? []).map((p) => p.id)),
    [treeGroups],
  );

  const toggleParent = (id: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedParents(new Set(allParentIds));
  const collapseAll = () => setExpandedParents(new Set());

  // Top-level stakeholders available as parent candidates
  const parentCandidates = useMemo(
    () => items.filter((i) => !i.parent_id && i.id !== editItem?.id),
    [items, editItem],
  );

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post(`/master/${activeTab}`, data),
    onSuccess: () => {
      resetForm();
      setIsRefreshing(true);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["master", activeTab] });
        setIsRefreshing(false);
      }, 3000);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail ?? "Terjadi kesalahan saat menyimpan data.";
      setErrorModal({ open: true, title: "Gagal Menyimpan", message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) =>
      api.put(`/master/${activeTab}/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["master", activeTab] });
      resetForm();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail ?? "Terjadi kesalahan saat menyimpan perubahan.";
      setErrorModal({ open: true, title: "Gagal Menyimpan", message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/master/${activeTab}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["master", activeTab] }),
    onError: (error: any) => {
      const message = error?.response?.data?.detail ?? "Terjadi kesalahan saat menghapus data.";
      setErrorModal({ open: true, title: "Gagal Menghapus", message });
    },
  });

  const resetForm = () => {
    setShowModal(false);
    setEditItem(null);
    setFormData({ kode: "", nama: "", tipe: "", deskripsi: "", urutan: 1, parent_id: "" });
  };

  const handleEdit = (item: MasterItem) => {
    setEditItem(item);
    setFormData({
      kode: item.kode || "",
      nama: item.nama,
      tipe: item.tipe || "",
      deskripsi: item.deskripsi || "",
      urutan: item.urutan || 1,
      parent_id: item.parent_id || "",
    });
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let payload: Record<string, unknown> = {
      nama: formData.nama,
      deskripsi: formData.deskripsi,
    };
    if (hasKode) payload = { ...payload, kode: formData.kode, urutan: formData.urutan };
    if (hasTipe) payload = { ...payload, tipe: formData.tipe };
    if (hasParent) payload = { ...payload, parent_id: formData.parent_id || null };

    if (editItem) {
      updateMutation.mutate({ id: editItem.id, data: payload as typeof formData });
    } else {
      createMutation.mutate(payload as typeof formData);
    }
  };

  // Column count for colspan (no "Parent" column in tree view — hierarchy is shown inline)
  const colCount = (hasKode ? 1 : 0) + (hasTipe ? 1 : 0) + 3; // nama + deskripsi + aksi

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Master Data</h1>
          <p className="text-sm text-gray-500 mt-0.5">Kelola data referensi</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Tambah
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setSortKode(null); setSortNama(null); resetForm(); }}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.key
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Add / Edit Modal — rendered via portal to <body> so the backdrop
          covers the full viewport (incl. navbar), not just the content area. */}
      {showModal && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) resetForm(); }}
        >
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">
                {editItem ? "Edit" : "Tambah"} {TABS.find((t) => t.key === activeTab)?.label}
              </h3>
              <button
                onClick={resetForm}
                className="rounded p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {hasKode && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Kode</label>
                  <input
                    value={formData.kode}
                    onChange={(e) => setFormData({ ...formData, kode: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>
              )}

              {hasParent && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Parent Stakeholder
                  </label>
                  <select
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">— Top Level (tanpa parent) —</option>
                    {parentCandidates.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.nama}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nama</label>
                <input
                  value={formData.nama}
                  onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  required
                />
              </div>

              {hasTipe && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tipe</label>
                  <select
                    value={formData.tipe}
                    onChange={(e) => setFormData({ ...formData, tipe: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Pilih tipe</option>
                    <option value="INTERNAL">INTERNAL</option>
                    <option value="EXTERNAL">EXTERNAL</option>
                  </select>
                </div>
              )}

              <div className={hasKode || hasParent || hasTipe ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-medium text-gray-600 mb-1">Deskripsi</label>
                <input
                  value={formData.deskripsi}
                  onChange={(e) => setFormData({ ...formData, deskripsi: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div className="sm:col-span-2 flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {editItem ? "Simpan Perubahan" : "Tambah"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      )}

      {/* Table */}
      {isLoading || isRefreshing ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {/* Expand/Collapse toolbar — only for hierarchy tabs */}
          {hasParent && (treeGroups?.topLevel.length ?? 0) > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50/50">
              <span className="text-xs text-gray-400">
                {treeGroups!.topLevel.length} stakeholder
                {treeGroups!.topLevel.length !== items.length && ` · ${items.length - treeGroups!.topLevel.length} sub-stakeholder`}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={expandAll}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                  Buka Semua
                </button>
                <button
                  onClick={collapseAll}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  <ChevronsDownUp className="h-3.5 w-3.5" />
                  Tutup Semua
                </button>
              </div>
            </div>
          )}

          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                {hasKode && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    <button
                      type="button"
                      onClick={() => { setSortNama(null); setSortKode((prev) => (prev === "asc" ? "desc" : "asc")); }}
                      className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                    >
                      Kode
                      {sortKode === null ? (
                        <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
                      ) : sortKode === "asc" ? (
                        <ArrowUp className="h-3.5 w-3.5 text-blue-500" />
                      ) : (
                        <ArrowDown className="h-3.5 w-3.5 text-blue-500" />
                      )}
                    </button>
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  <button
                    type="button"
                    onClick={() => { setSortKode(null); setSortNama((prev) => (prev === "asc" ? "desc" : prev === "desc" ? null : "asc")); }}
                    className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                  >
                    Nama
                    {sortNama === null ? (
                      <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
                    ) : sortNama === "asc" ? (
                      <ArrowUp className="h-3.5 w-3.5 text-blue-500" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 text-blue-500" />
                    )}
                  </button>
                </th>
                {hasTipe && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Tipe
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Deskripsi
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {/* ── Tree view for hierarchy tabs ── */}
              {hasParent && treeGroups && (() => {
                const rows: React.ReactNode[] = [];

                for (const parent of treeGroups.topLevel) {
                  const children = treeGroups.childMap.get(parent.id) ?? [];
                  const isExpanded = expandedParents.has(parent.id);
                  const hasChildren = children.length > 0;

                  // Parent row
                  rows.push(
                    <tr key={parent.id} className="hover:bg-gray-50 group">
                      <td className="px-4 py-3 text-gray-800">
                        <div className="flex items-center gap-2">
                          {/* Expand toggle */}
                          <button
                            onClick={() => hasChildren && toggleParent(parent.id)}
                            className={`flex-shrink-0 rounded p-0.5 transition-colors ${
                              hasChildren
                                ? "text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer"
                                : "text-gray-200 cursor-default"
                            }`}
                          >
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />}
                          </button>
                          <div>
                            <span className="font-medium text-gray-800">{parent.nama}</span>
                            {hasChildren && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-500">
                                {children.length}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      {hasTipe && (
                        <td className="px-4 py-3">
                          <TipeBadge tipe={parent.tipe} />
                        </td>
                      )}
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">
                        {parent.deskripsi || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RowActions item={parent} onEdit={handleEdit} onDelete={(id) =>
                          confirm({ title: "Hapus Item", message: "Item ini akan dihapus secara permanen. Lanjutkan?", confirmLabel: "Hapus", variant: "danger", onConfirm: () => deleteMutation.mutate(id) })
                        } />
                      </td>
                    </tr>
                  );

                  // Child rows — only rendered when expanded
                  if (isExpanded) {
                    for (const child of children) {
                      rows.push(
                        <tr key={child.id} className="bg-slate-50/70 hover:bg-slate-50 border-l-2 border-l-slate-200">
                          <td className="py-2.5 pr-4 pl-12 text-gray-600">
                            <div className="flex items-center gap-1.5">
                              <ChevronRight className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
                              <span className="text-sm">{child.nama}</span>
                            </div>
                          </td>
                          {hasTipe && (
                            <td className="px-4 py-2.5">
                              <TipeBadge tipe={child.tipe} />
                            </td>
                          )}
                          <td className="px-4 py-2.5 text-gray-400 text-xs max-w-xs truncate">
                            {child.deskripsi || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <RowActions item={child} onEdit={handleEdit} onDelete={(id) =>
                              confirm({ title: "Hapus Item", message: "Item ini akan dihapus secara permanen. Lanjutkan?", confirmLabel: "Hapus", variant: "danger", onConfirm: () => deleteMutation.mutate(id) })
                            } />
                          </td>
                        </tr>
                      );
                    }
                  }
                }

                // Orphaned items
                for (const item of treeGroups.orphans) {
                  rows.push(
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-800">
                        <div className="flex items-center gap-2 pl-7">
                          <span className="font-medium">{item.nama}</span>
                        </div>
                      </td>
                      {hasTipe && <td className="px-4 py-3"><TipeBadge tipe={item.tipe} /></td>}
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.deskripsi || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <RowActions item={item} onEdit={handleEdit} onDelete={(id) =>
                          confirm({ title: "Hapus Item", message: "Item ini akan dihapus secara permanen. Lanjutkan?", confirmLabel: "Hapus", variant: "danger", onConfirm: () => deleteMutation.mutate(id) })
                        } />
                      </td>
                    </tr>
                  );
                }

                if (rows.length === 0) {
                  return (
                    <tr>
                      <td colSpan={colCount} className="px-4 py-12 text-center text-gray-400 text-sm">
                        Tidak ada data
                      </td>
                    </tr>
                  );
                }

                return rows;
              })()}

              {/* ── Flat view for non-hierarchy tabs ── */}
              {!hasParent && flatItems.map(({ item }) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  {hasKode && (
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-600">
                      {item.kode}
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium text-gray-800">{item.nama}</td>
                  {hasTipe && (
                    <td className="px-4 py-3"><TipeBadge tipe={item.tipe} /></td>
                  )}
                  <td className="px-4 py-3 text-gray-500 text-xs">{item.deskripsi || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <RowActions item={item} onEdit={handleEdit} onDelete={(id) =>
                      confirm({ title: "Hapus Item", message: "Item ini akan dihapus secara permanen. Lanjutkan?", confirmLabel: "Hapus", variant: "danger", onConfirm: () => deleteMutation.mutate(id) })
                    } />
                  </td>
                </tr>
              ))}

              {!hasParent && flatItems.length === 0 && (
                <tr>
                  <td colSpan={colCount} className="px-4 py-12 text-center text-gray-400 text-sm">
                    Tidak ada data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal {...modalProps} />

      <ConfirmModal
        open={errorModal.open}
        variant="warning"
        title={errorModal.title}
        message={errorModal.message}
        confirmLabel="Mengerti"
        cancelLabel=""
        onConfirm={() => setErrorModal({ open: false, title: "", message: "" })}
        onCancel={() => setErrorModal({ open: false, title: "", message: "" })}
      />
    </div>
  );
}
